import { Names, Values } from '@mode-switch/model';
import { EncField, Encoding, EncTotals, Guide, VariableType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { SwitchSchemaModel } from './schema-model';

export const TOTALS_TOKEN = '$ALL$';
export const GRAND_TOTALS_LABEL = 'Grand Totals';
export const TOTAL_LABEL = 'Total';
export const SUBTOTAL_TOKEN = '$$SUBTOTAL_ALL$$';

const guide = new Guide();

export function enableColGrandTotal(enc: Encoding) {
  return guide.enableGrandTotal(enc, EncTotals.Direction.Column);
}

export function disableColGrandTotal(enc: Encoding) {
  return guide.disableGrandTotal(enc, EncTotals.Direction.Column);
}

export function enableRowGrandTotal(enc: Encoding) {
  return guide.enableGrandTotal(enc, EncTotals.Direction.Row);
}

export function disableRowGrandTotal(enc: Encoding) {
  return guide.disableGrandTotal(enc, EncTotals.Direction.Row);
}

export function enableColSubtotals(enc: Encoding) {
  return guide.enableSubtotals(enc, EncTotals.Direction.Column);
}

export function disableColSubtotals(enc: Encoding) {
  return guide.disableSubtotals(enc, EncTotals.Direction.Column);
}

export function enableRowSubtotals(enc: Encoding) {
  return guide.enableSubtotals(enc, EncTotals.Direction.Row);
}

export function disableRowSubtotals(enc: Encoding) {
  return guide.disableSubtotals(enc, EncTotals.Direction.Row);
}

/**
 * @description Returns an object indicating which grand totals
 * the given encoding is candidate for. This function DOES NOT
 * check whether row|col grand totals have been enabled in the
 * encoding; rather, it validates that the encoding could produce
 * a valid grand total query for any of { row, col, aggFilter, all }
 */
export function getGrandTotalCandidacy(enc: Encoding, model: FlamingoTypes.ModeModel, viewName: string) {
  const totals = {
    aggFilter: false,
    col: false,
    row: false,
    all: false,
  };

  if (enc == null) {
    return totals;
  }

  const switchModel = SwitchSchemaModel.make(model, viewName);
  if (switchModel == null) {
    return totals;
  }

  /**
   * @description Returns true if a discrete, non-names field
   * can be found in the provided channels; false otherwise
   */
  const hasDiscrete = (channels: (readonly EncField[])[]) => {
    return channels.some((fields) => {
      return fields.some((f) => f.variable.type === VariableType.DISCRETE && f.formula !== Names.Key);
    });
  };

  /**
   * @description Returns true if a continuous, non-values field
   * can be found in the provided channels; false otherwise
   */
  const hasContinuous = (channels: (readonly EncField[])[]) => {
    return channels.some((fields) => {
      return fields.some((f) => f.variable.type === VariableType.CONTINUOUS && f.formula !== Values.Key);
    });
  };

  /**
   * @description Returns true if a discrete measure field can
   * be found in the provided channels; false otherwise
   */
  const hasDiscreteMeasure = (channels: (readonly EncField[])[]) => {
    return channels.some((fields) => {
      return fields.some((f) => {
        const compiledField = switchModel.compileEncField(f);
        return compiledField.isValid && compiledField.isDiscrete && compiledField.isAggregateStage;
      });
    });
  };

  const hasSomeAggFilters = enc.filter.some((f) => !switchModel.compileEncField(f).isAggregateable);
  if (hasSomeAggFilters) {
    totals.aggFilter = true;
  }

  // Shared channels
  const markChannels = [
    ...enc.marks.properties.color.values,
    ...enc.marks.properties.text.values,
    ...enc.marks.properties.label.values,
    ...enc.marks.properties.size.values,
    ...enc.marks.properties.detail.values,
    ...enc.marks.properties.angle.values,
    ...enc.marks.properties.baseline.values,
  ];

  /**
   * The existence of a continuous value in the encoding
   * is common to each grand total assertion: col, row, all
   */
  const hasOneOrMoreContinuousValue = hasContinuous([
    enc.column,
    enc.x,
    enc.x2,
    enc.row,
    enc.y,
    enc.y2,
    enc.value,
    markChannels,
  ]);

  /**
   * Column Grand Totals
   *
   * 1. Has 1+ discrete value on channels { row, y }
   * 2. - Has 0 discrete measures on channels { column, x }
   *    - Has 1+ continuous value on any channel,
   *    - OR, has 1+ discrete value on any channel ∉ { row, y }
   */
  if (hasDiscrete([enc.row, enc.y])) {
    totals.col =
      !hasDiscreteMeasure([enc.column, enc.x]) &&
      (hasOneOrMoreContinuousValue || hasDiscrete([enc.column, enc.x, markChannels]));
  }

  /**
   * Row Grand Totals
   *
   * 1. Has 1+ discrete value on channels { column, x }
   * 2. - Has 0 discrete measures on channels { row, y }
   *    - Has 1+ continuous value on any channel,
   *    - OR, has 1+ discrete value on any channel ∉ { column, x }
   */
  if (hasDiscrete([enc.column, enc.x])) {
    totals.row =
      !hasDiscreteMeasure([enc.row, enc.y]) &&
      (hasOneOrMoreContinuousValue || hasDiscrete([enc.row, enc.y, markChannels]));
  }

  /**
   * Combined Grand Totals
   *
   * 1. Both Column and Row Grand Totals are possible
   * 2. - Has 1+ continuous values on any channel
   *    - OR, has 1+ discrete values on any channel ∉ { column, x, row, y }
   */
  if (totals.col && totals.row) {
    totals.all = hasOneOrMoreContinuousValue || hasDiscrete([markChannels]);
  }

  return totals;
}

/**
 * @description Returns an object indicating which subtotals
 * the given encoding is candidate for. This function DOES NOT
 * check whether row|col subtotals have been enabled in the
 * encoding; rather, it validates that the encoding could produce
 * a valid subtotal query for any of { row, col, aggFilter, all }
 */
export function getSubtotalsCandidacy(enc: Encoding, model: FlamingoTypes.ModeModel, viewName: string) {
  // Subtotals follow the same rules as totals except:
  //   - There must be > 1 discrete fields on the channel

  const isNonNameDiscrete = (field: Readonly<EncField>) =>
    field.variable.type === VariableType.DISCRETE && field.formula !== Names.Key;

  const hasMultipleDiscrete = (channels: (readonly EncField[])[]) => {
    return channels.reduce((n, next = []) => n + next.filter((f) => isNonNameDiscrete(f)).length, 0) > 1;
  };

  // Start with the grand totals
  const totals = getGrandTotalCandidacy(enc, model, viewName);

  const subtotals = {
    ...totals,
    col: totals.col && hasMultipleDiscrete([enc.row, enc.y]),
    row: totals.row && hasMultipleDiscrete([enc.column, enc.x]),
  };
  subtotals.all = totals.all && subtotals.col && subtotals.row;

  return subtotals;
}

export function isTotalableEncField(field: EncField) {
  return field.variable.type === VariableType.DISCRETE && field.formula !== Names.Key;
}

export function canSubtotalEncFieldChannels(channels: (readonly EncField[] | undefined)[]) {
  return channels.reduce((n, next = []) => n + next.filter(isTotalableEncField).length, 0) > 1;
}

export function dropLastTotalableField(fields: readonly EncField[]) {
  const result = fields.slice();

  for (let i = result.length; i--; ) {
    const field = result[i];
    if (isTotalableEncField(field)) {
      result.splice(i, 1);
      break;
    }
  }

  return result;
}
